import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { LEGAL_DOCS, LEGAL_LINKS } from "../lib/legal";

const LegalPage = () => {
  const { slug } = useParams();
  const { pick, lang } = useI18n();
  const doc = LEGAL_DOCS[slug];
  if (!doc) return <Navigate to="/legal/kvkk" replace />;

  return (
    <div data-testid={`legal-page-${slug}`}>
      {/* Compact header — no big banner, page starts right below site nav */}
      <section className="pt-32 md:pt-36 pb-4 md:pb-6 bg-white">
        <div className="container-x">
          <nav className="flex items-center gap-2 text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-brand-primary transition-colors">
              {pick({ tr: "Ana Sayfa", en: "Home", ar: "الرئيسية" })}
            </Link>
            <ChevronRight size={12} strokeWidth={1.5} />
            <span className="text-brand-navy">{pick(doc.title)}</span>
          </nav>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-x pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          {/* Sticky sidebar navigation */}
          <aside className="lg:col-span-3 hidden lg:block">
            <div className="sticky top-32">
              <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-medium mb-5">
                {pick({ tr: "Hukuki Metinler", en: "Legal Documents", ar: "الوثائق القانونية" })}
              </div>
              <nav className="space-y-1">
                {LEGAL_LINKS.map((l) => {
                  const d = LEGAL_DOCS[l.slug];
                  const on = l.slug === slug;
                  return (
                    <Link
                      key={l.slug}
                      to={`/legal/${l.slug}`}
                      className={`block px-4 py-3 border-l-2 text-sm transition-all ${
                        on
                          ? "border-brand-primary bg-brand-light text-brand-navy font-medium"
                          : "border-transparent text-brand-medium hover:border-brand-primary/40 hover:text-brand-navy hover:bg-brand-light/60"
                      }`}
                      data-testid={`legal-nav-${l.slug}`}
                    >
                      {pick(d.title)}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <article className="lg:col-span-9">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
            >
              <div className="flex items-center gap-4 mb-5">
                <span className="h-px w-12 bg-brand-primary" />
                <span className="text-[0.7rem] tracking-[0.36em] uppercase text-brand-primary font-medium">
                  {pick({ tr: "Hukuki", en: "Legal", ar: "قانوني" })}
                </span>
              </div>
              <h1
                className="font-serif text-brand-navy leading-[1.05] tracking-tight"
                style={{ fontSize: "clamp(1.9rem, 3.8vw, 3rem)" }}
              >
                {pick(doc.title)}
              </h1>
              <p className="mt-6 text-brand-dark leading-[1.75] max-w-3xl">{pick(doc.intro)}</p>

              <div className="mt-12 space-y-10">
                {doc.sections.map((sec, i) => (
                  <div
                    key={i}
                    className="pt-8 border-t border-black/10"
                    data-testid={`legal-section-${i}`}
                  >
                    <h2 className="font-serif text-brand-navy text-xl md:text-2xl leading-tight mb-4">
                      {pick(sec.heading)}
                    </h2>
                    <div
                      className="text-brand-dark leading-[1.8] whitespace-pre-line"
                      style={{ fontSize: "clamp(0.95rem, 1vw, 1rem)" }}
                    >
                      {pick(sec.body)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile-only bottom nav to other legal docs */}
              <div className="mt-16 pt-8 border-t border-black/10 lg:hidden">
                <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-medium mb-4">
                  {pick({ tr: "Diğer Metinler", en: "Other documents", ar: "الوثائق الأخرى" })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {LEGAL_LINKS.filter((l) => l.slug !== slug).map((l) => {
                    const d = LEGAL_DOCS[l.slug];
                    return (
                      <Link
                        key={l.slug}
                        to={`/legal/${l.slug}`}
                        className="px-4 py-2 border border-black/15 text-xs text-brand-navy hover:border-brand-navy transition-colors"
                      >
                        {pick(d.title)}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;
